import { useEffect, useState } from "react";
import { CircleNotch, DownloadSimple, List, X } from "@phosphor-icons/react";
import { useDownload } from "@/lib/download";

const NAV_LINKS = [
  { label: "Rates", href: "#rates", testId: "nav-rates-link" },
  { label: "How It Works", href: "#how-it-works", testId: "nav-how-it-works-link" },
  { label: "Why Coin Adda", href: "#why-coin-adda", testId: "nav-why-link" },
  { label: "FAQ", href: "#faq", testId: "nav-faq-link" },
];

export const scrollToSection = (hash) => {
  document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Header() {
  const { startDownload, downloading } = useDownload();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    scrollToSection(href);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-[background-color,border-color] duration-500 ${
        scrolled ? "border-white/10 bg-[#0F172A]/80" : "border-transparent bg-[#050B14]/40"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <a
          href="#top"
          data-testid="header-logo-link"
          onClick={(e) => handleNav(e, "#top")}
          className="flex items-center"
        >
          <img
            src="/assets/coin-adda-logo.png"
            alt="Coin Adda — USDT to INR Exchange"
            className="h-9 w-auto sm:h-11"
            data-testid="header-logo"
          />
        </a>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={link.testId}
              onClick={(e) => handleNav(e, link.href)}
              className="group relative text-sm font-medium text-slate-300 transition-colors duration-300 hover:text-white"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#00F5A0] transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="header-download-btn"
            onClick={startDownload}
            disabled={downloading}
            className="group hidden items-center gap-2 rounded-full bg-[#00F5A0] px-5 py-2.5 text-sm font-semibold text-[#050B14] transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#00D68B] hover:shadow-[0_8px_30px_rgba(0,245,160,0.35)] disabled:opacity-70 sm:inline-flex"
          >
            {downloading ? (
              <CircleNotch size={18} weight="bold" className="animate-spin" />
            ) : (
              <DownloadSimple size={18} weight="bold" />
            )}
            {downloading ? "Starting…" : "Download App"}
          </button>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setMenuOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-slate-200 lg:hidden"
          >
            {menuOpen ? <X size={20} /> : <List size={20} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="border-t border-white/10 bg-[#0F172A]/95 px-5 py-6 backdrop-blur-xl lg:hidden"
        >
          <nav className="flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`mobile-${link.testId}`}
                onClick={(e) => handleNav(e, link.href)}
                className="text-base font-medium text-slate-200"
              >
                {link.label}
              </a>
            ))}
            <button
              type="button"
              data-testid="mobile-menu-download-btn"
              onClick={() => {
                setMenuOpen(false);
                startDownload();
              }}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#00F5A0] px-5 py-3 text-sm font-semibold text-[#050B14]"
            >
              <DownloadSimple size={18} weight="bold" />
              Download App
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
